/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import React, { FC, useEffect, useRef, useState, KeyboardEvent } from 'react';
import clsx from 'clsx';

import {
  DropDownModal,
  CustomSelectBlock,
  RadioButton,
  InputTextField,
  shouldIgnoreClickOutside,
  shouldIgnoreFocusOutside,
} from '@marriott/mi-ui-library';

import { SpecialRatesProps } from './NGSpecialRatesAndPoints.types';
import { StyledSpecialRatesDiv } from './NGSpecialRatesAndPoints.styles';
import { NGPointsToolTip } from '../NGPointsToolTip/NGPointsToolTip';
import { EEO_MEMBER_CODES } from '../MPropertyPriceSection/MPropertyPriceSection.constants';

import {
  SPECIAL_RATES,
  USE_POINTS,
  FOCUS_INTERACTIONS,
  CORP,
  NG_SPECIAL_RATES_DONE_CLICK_TRACK,
  SPECIAL_RATES_CLOSE_CLICK_TRACK,
  GROUP_CODE_LENGTH,
  SPECIAL_RATES_CODE_LENGTH,
  SPECIAL_RATES_CODE_MIN_LENGTH,
} from '../../store/store.constants';
import { SearchFormActiveState } from '../../store/ngDatepickerConstants';
import { useSearchFormStore } from '../../store/searchFormStore';
import { useStaticDataContext } from '../../context/StaticDataContext';
import { ELEMENT_BUTTON, ELEMENT_INPUT, searchFormClickTrackingLoc } from '../../utils/ngSearchConstants';
import { measureTextWidth } from '../../utils/searchUtils';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    custom_click_track_value?: string;
  }
}

export const NGSpecialRatesAndPoints: FC<SpecialRatesProps> = ({
  onCancel,
  onChange,
  isDesktopView,
  addLabelSpacingForArcTool = false,
  isAvailabilityScenario,
  errorMessage,
  isNextGenHP,
  isSecondaryBlockEnabled = true,
  onInteraction,
  externalCloseSignal,
  isInModal,
  currentFormState,
  forceOpen = false,
  showDesktopFooter = false,
  applyTrackLabel,
}) => {
  const compRef = useRef<HTMLDivElement>(null);
  const setSearchFormState = useSearchFormStore((state: any) => state.setSearchFormState);
  const splRatesHiddenFields = useSearchFormStore((state: any) => state.specialRates);
  const usePointsValue = useSearchFormStore((state: any) => state.usePoints);
  const [popupOpenState, setPopupOpenState] = React.useState(forceOpen);
  const searchFormTopPosition = useSearchFormStore((state: any) => state?.searchFormTopPosition);
  // Track if Apply button was clicked (to prevent onCancel from being called after Apply)
  const applyClickedRef = useRef(false);

  // Reset applyClickedRef on every fresh open triggered by a forceOpen prop change.
  useEffect(() => {
    if (forceOpen) {
      applyClickedRef.current = false;
      setPopupOpenState(true);
    }
  }, [forceOpen]);
  const isSearchFormActiveState = useSearchFormStore((state: any) => state?.isSearchFormActiveState);
  const setIsSearchFormActiveState = useSearchFormStore((state: any) => state?.setIsSearchFormActiveState);
  const activePopup = useSearchFormStore((state: any) => state?.activePopup);
  const setActivePopup = useSearchFormStore((state: any) => state?.setActivePopup);

  // Track if component has mounted on client to fix SSR hydration mismatch
  // SSR returns fallback width (text.length * 10), client measures actual font width
  const [isClientMounted, setIsClientMounted] = useState(false);
  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  // state to show or hide input field on selection of corp code
  const [shorCorpCodeInputField, setShowCorpCodeInputField] = useState(false);

  const {
    specialRatesListItems,
    specialRatesEyebrowText,
    enterCodeEyebrowText,
    specialRatesApplyCta,
    usePointsCertificates,
    disableUsePointCertificates,
    pointsAwardsTitle,
    showPointsTooltip,
    pointsToolTipCTALabel = 'Got it',
    pointsToolTipMessage = 'Find them in the Rates & Points menu.',
    pointsToolTipTitle = 'Want to use points or awards?',
  } = useStaticDataContext();

  //TODO- Move corpCodeValue and groupValue to searchform constant file and import it from there
  const corpCodeValue = 'corp';
  const groupValue = 'group';
  const specialRatesList = (specialRatesListItems as Array<Record<string, string>>) ?? [];
  // Build computed list including Points option as a radio when enabled
  const pointsSpecialRatesList: Array<Record<string, string>> = [
    // Add Points first when available
    ...(!disableUsePointCertificates
      ? [
          {
            specialRatesCode: 'points',
            specialRateslabel: usePointsCertificates,
          } as Record<string, string>,
        ]
      : []),
    ...specialRatesList,
  ];
  let specialRateDefaultLabel = '';
  specialRatesList?.forEach(obj => {
    if (obj['specialRatesCode'] === splRatesHiddenFields?.specialRateCode) {
      specialRateDefaultLabel = obj['specialRateslabel'];
    }
  });
  // If Points (rewards redemption) is active, surface that label in the summary
  if (usePointsValue?.rewardsRedemption && !disableUsePointCertificates) {
    specialRateDefaultLabel = usePointsCertificates;
  }
  const [showIcon, setShowIcon] = useState(
    splRatesHiddenFields?.corporateCode || splRatesHiddenFields?.groupCode ? true : false
  );
  const [showPointsToolTip, setShowPointsToolTip] = useState(false);
  const hasShownPointsToolTipRef = useRef(false);

  const bodyRef = useRef<HTMLBodyElement | null>(null);
  const customSelectRef = useRef<HTMLDivElement | null>(null);
  const [buttonOffset, setButtonOffset] = React.useState<number>(0);

  useEffect(() => {
    bodyRef.current = document.body as HTMLBodyElement;
  }, []);

  const hidePointsToolTip = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('hidePointsToolTip', 'true');
    }
    setShowPointsToolTip(false);
  };

  useEffect(() => {
    // Skip in SSR; on desktop only show tooltip when secondary block is enabled
    if (typeof window === 'undefined' || (isDesktopView && !isSecondaryBlockEnabled)) {
      return;
    }

    const hidePointsTooltipFlag = window.sessionStorage.getItem('hidePointsToolTip');
    const isTooltipEnabled = Boolean(showPointsTooltip);

    // Only show tooltip if BOTH conditions are met:
    // 1. AEM author has enabled the tooltip via the "Show Points Tool Tip" checkbox
    // 2. User hasn't dismissed it in this session (session storage check)
    if (isTooltipEnabled && !hidePointsTooltipFlag) {
      setShowPointsToolTip(true);
    }
  }, [isSecondaryBlockEnabled, isDesktopView, showPointsTooltip]);

  useEffect(() => {
    if (showPointsToolTip) {
      const isNextGenHPMobile = Boolean(isNextGenHP && !isDesktopView);
      if (!isNextGenHPMobile || currentFormState === SearchFormActiveState.FORM_STATE) {
        hasShownPointsToolTipRef.current = true;
      }
    }
  }, [showPointsToolTip, currentFormState, isNextGenHP, isDesktopView]);

  useEffect(() => {
    if (!externalCloseSignal) {
      return;
    }
    if (hasShownPointsToolTipRef.current) {
      hidePointsToolTip();
    }
  }, [externalCloseSignal]);

  const clickTrackingLoc = 'Search Form';

  // to toggle popup on desktop on button click
  const showPopup = (): void => {
    onInteraction?.();

    // Calculate button offset only when needed and only for NextGen HP Desktop
    if (isDesktopView && isNextGenHP && customSelectRef.current && buttonOffset === 0) {
      const button = customSelectRef.current.querySelector('button');
      if (button) {
        setButtonOffset(button.offsetLeft);
      }
    }

    const newState = !popupOpenState;
    setPopupOpenState(newState);
    if (newState) {
      setActivePopup('specialRates');
    } else if (activePopup === 'specialRates') {
      setActivePopup(null);
    }
    //capture user interactions
    setSearchFormState([FOCUS_INTERACTIONS], { [FOCUS_INTERACTIONS]: { isUserInteractWithComp: true } });
  };

  useEffect(() => {
    //while opening th Rates & Points popup , if corp/group code is selected and is present then show the input field
    if (
      popupOpenState &&
      (splRatesHiddenFields?.corporateCode ||
        splRatesHiddenFields?.groupCode ||
        splRatesHiddenFields?.specialRateCode === 'corp' ||
        splRatesHiddenFields?.specialRateCode === 'group')
    ) {
      setShowCorpCodeInputField(true);
    } else {
      setShowCorpCodeInputField(false);
    }
  }, [popupOpenState]);

  let blurTimeout: string | number | NodeJS.Timeout | undefined; // a timeout function to handle blur after specific amount of time

  const handleIconPopUpClick = (event: KeyboardEvent<Element>): void => {
    event.preventDefault(); // preventing default action so that on focus event does not interfer with on click event
    clearTimeout(blurTimeout); // clearing timeout
    onInteraction?.();
    const newState = !popupOpenState;
    setPopupOpenState(newState);
    if (newState) {
      setActivePopup('specialRates');
    } else if (activePopup === 'specialRates') {
      setActivePopup(null);
    }
    //capture user interactions
    setSearchFormState([FOCUS_INTERACTIONS], { [FOCUS_INTERACTIONS]: { isUserInteractWithComp: true } });
  };
  const inputRef = useRef<HTMLInputElement>(null); // ref to point parent div of input element

  // Function to close dropdown when focus leaves both input and dropdown
  const handleBlur = (event: any) => {
    const relatedTarget = event.relatedTarget as Element;
    // Don't close if focus moved to an external modal (privacy popup, Adobe experiments, etc.)
    if (shouldIgnoreFocusOutside(relatedTarget)) {
      return;
    }
    if (relatedTarget && !inputRef?.current?.contains(relatedTarget) && !compRef.current?.contains(relatedTarget)) {
      setPopupOpenState(false);
      if (activePopup === 'specialRates') {
        setActivePopup(null);
      }
    }
  };

  useEffect(() => {
    if (popupOpenState || !isDesktopView) {
      if (
        splRatesHiddenFields?.corporateCode ||
        splRatesHiddenFields?.groupCode ||
        splRatesHiddenFields?.specialRateCode === 'corp' ||
        splRatesHiddenFields?.specialRateCode === 'group'
      ) {
        const textField = compRef.current?.querySelector(`[maxLength="9"][type="text"]`) as HTMLElement;
        textField?.focus();
      } else {
        setFocusOnFirstBtn();
      }
    }
  }, [shorCorpCodeInputField]);

  useEffect(() => {
    /**
     * Disable the background scroll when popup is open in Tabbed Searchform
     * */
    if (bodyRef.current) {
      const isMapViewActive = sessionStorage.getItem('map_view') === 'true';

      if (!isDesktopView) {
        bodyRef.current.style.overflow = 'hidden'; // Disable scrolling on the background
      } else if (!isMapViewActive) {
        bodyRef.current.style.overflow = 'auto';
      }
    }

    const isCorpOrGroup =
      splRatesHiddenFields?.specialRateCode === 'corp' || splRatesHiddenFields?.specialRateCode === 'group';
    const hasNoCode =
      (splRatesHiddenFields?.specialRateCode === 'corp' && !splRatesHiddenFields?.corporateCode?.trim()) ||
      (splRatesHiddenFields?.specialRateCode === 'group' && !splRatesHiddenFields?.groupCode?.trim());
    if (!popupOpenState && isCorpOrGroup && hasNoCode) {
      setSearchFormState([SPECIAL_RATES], {
        [SPECIAL_RATES]: {
          specialRateCode: 'none',
          corporateCode: '',
          groupCode: '',
          clusterCode: 'Lowest Regular Rate',
        },
      });
    }
  }, [popupOpenState, isDesktopView]);

  const setFocusOnFirstBtn = (): void => {
    const popupElemBtn = compRef.current?.querySelector(
      ".special-rates-popup .radio-label input[type='radio']"
    ) as HTMLElement;
    popupElemBtn?.focus();
  };

  const keyHandler = (value: string): void => {
    if (!value) {
      const textField = compRef.current?.querySelector('[name="corporateCode"]') as HTMLElement;
      textField?.focus();
    }
  };

  useEffect(() => {
    // Focus on the textfiled, When corp option is selected
    if (splRatesHiddenFields?.specialRateCode?.toLowerCase() === CORP) {
      const textField = compRef.current?.querySelector('[name="input-text-ENTER CODE"]') as HTMLElement;
      textField?.focus();
    }
  }, [splRatesHiddenFields?.specialRateCode]);

  // to handle radioButton selections
  const changeHandler = (value: string, label: string, code: string): void => {
    // Handle Points radio selection similar to checkbox behavior
    if (value?.toLowerCase() === 'points') {
      setSearchFormState([SPECIAL_RATES, USE_POINTS, FOCUS_INTERACTIONS], {
        [SPECIAL_RATES]: {
          clusterCode: 'Lowest Regular Rate',
          specialRateCode: 'Lowest Regular Rate',
          corporateCode: '',
          groupCode: '',
        },
        [USE_POINTS]: {
          rewardsRedemption: true,
        },
        [FOCUS_INTERACTIONS]: { isUserInteractWithComp: true },
      });
      setShowCorpCodeInputField(false);

      // Auto-close only when NOT using the desktop footer (Apply button handles close there)
      if (isDesktopView && !showDesktopFooter) {
        setPopupOpenState(false);
        if (activePopup === 'specialRates') {
          setActivePopup(null);
        }
      }
      return;
    }

    setSearchFormState([SPECIAL_RATES, USE_POINTS], {
      [SPECIAL_RATES]: {
        specialRateCode: value,
        corporateCode: '',
        groupCode: '',
        clusterCode: label,
      },
      [USE_POINTS]: {
        rewardsRedemption: false,
      },
    });
    // to hide the textfield when corp/group option is not selected
    setShowCorpCodeInputField(false);
    // to show the textfield when corp/group option is selected
    if (
      (code?.toLowerCase() === corpCodeValue || code === groupValue) &&
      code?.toLowerCase() === value?.toLowerCase()
    ) {
      setShowCorpCodeInputField(true);
    } else {
      // Auto-close only when NOT using the desktop footer (Apply button handles close there)
      if (isDesktopView && !showDesktopFooter && code?.toLowerCase() !== corpCodeValue && code !== groupValue) {
        setPopupOpenState(false);
        if (activePopup === 'specialRates') {
          setActivePopup(null);
        }
      }
    }
  };

  const returnDescription = () => {
    // Show Points label when rewards redemption is active
    if (usePointsValue?.rewardsRedemption && !disableUsePointCertificates) {
      return usePointsCertificates;
    }
    // For EEO pricing codes (E0P, E0S, E0G), show "Lowest Regular Rate" instead of the raw code
    const currentRateCode = splRatesHiddenFields?.specialRateCode;
    if (currentRateCode && EEO_MEMBER_CODES.includes(currentRateCode)) {
      const defaultRateItem = specialRatesList?.find(item => item['specialRatesCode'] === 'none');
      return defaultRateItem?.['specialRateslabel'] || 'Lowest Regular Rate';
    }
    let specialLabel = specialRateDefaultLabel;
    if (splRatesHiddenFields?.specialRateCode?.toLowerCase() === corpCodeValue) {
      if (splRatesHiddenFields?.corporateCode) {
        specialLabel = splRatesHiddenFields?.corporateCode;
      }
    }
    if (splRatesHiddenFields?.specialRateCode?.toLowerCase() === groupValue) {
      if (splRatesHiddenFields?.groupCode) {
        specialLabel = splRatesHiddenFields?.groupCode;
      }
    }
    return specialLabel;
  };
  const handleInputChange = (returnedValue: string) => {
    if (returnedValue) {
      setShowIcon(true);
    } else {
      setShowIcon(false);
    }
    const isGroupRate = splRatesHiddenFields?.specialRateCode === groupValue;
    setSearchFormState([SPECIAL_RATES], {
      [SPECIAL_RATES]: {
        ...splRatesHiddenFields,
        clusterCode: splRatesHiddenFields?.clusterCode,
        specialRateCode: splRatesHiddenFields?.specialRateCode,
        corporateCode: isGroupRate ? '' : returnedValue,
        groupCode: isGroupRate ? returnedValue : '',
      },
    });
  };
  const clearInput = () => {
    const isGroupRate = splRatesHiddenFields?.specialRateCode === groupValue;
    setSearchFormState([SPECIAL_RATES], {
      [SPECIAL_RATES]: {
        ...splRatesHiddenFields,
        corporateCode: isGroupRate ? splRatesHiddenFields?.corporateCode : '',
        groupCode: isGroupRate ? '' : splRatesHiddenFields?.groupCode,
      },
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>, value: string, label: string, code: string): void => {
    if (e.keyCode === 13) {
      if (value?.toLowerCase() === 'points') {
        setSearchFormState([SPECIAL_RATES, USE_POINTS, FOCUS_INTERACTIONS], {
          [SPECIAL_RATES]: {
            clusterCode: 'Lowest Regular Rate',
            specialRateCode: 'Lowest Regular Rate',
            corporateCode: '',
          },
          [USE_POINTS]: {
            rewardsRedemption: true,
          },
          [FOCUS_INTERACTIONS]: { isUserInteractWithComp: true },
        });
        setShowCorpCodeInputField(false);
        return;
      }
      setSearchFormState([SPECIAL_RATES, USE_POINTS], {
        [SPECIAL_RATES]: {
          specialRateCode: value,
          corporateCode: '',
          groupCode: '',
          clusterCode: label,
        },
        [USE_POINTS]: {
          rewardsRedemption: false,
        },
      });
      // to hide the textfield when corp/group option is not selected
      setShowCorpCodeInputField(false);
      // to show the textfield when corp/group option is selected
      if (
        (code?.toLowerCase() === corpCodeValue || code === groupValue) &&
        code?.toLowerCase() === value?.toLowerCase()
      ) {
        setShowCorpCodeInputField(true);
      }
    }
  };

  const handleIconClick = (e: KeyboardEvent<HTMLElement>) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      clearInput();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      // Only run click-outside logic when popup is actually open
      if (!popupOpenState) return;

      const target = event.target as Element;

      // Ignore clicks on external modals (privacy popup, Adobe experiments, etc.)
      if (shouldIgnoreClickOutside(target)) {
        return;
      }

      // Check if the click target is not within the popup or the CustomSelectBlock
      if (
        !target.closest('.dropdown-container') &&
        !target.closest('.dropdown-container-nextGenHP') &&
        !target.closest('.special-rates-input-container') &&
        !target.closest('.special-rates-arrow-icon') &&
        !(customSelectRef.current && customSelectRef.current.contains(target))
      ) {
        setPopupOpenState(false);
        if (activePopup === 'specialRates') {
          setActivePopup(null);
        }
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupOpenState, activePopup]);

  // Close popup when another popup becomes active (popup coordination)
  useEffect(() => {
    if (activePopup && activePopup !== 'specialRates' && popupOpenState) {
      setPopupOpenState(false);
    }
  }, [activePopup]);

  const isGroupCode = splRatesHiddenFields?.specialRateCode === groupValue;
  const groupCodeMaxLength = isAvailabilityScenario && isGroupCode ? GROUP_CODE_LENGTH : SPECIAL_RATES_CODE_LENGTH;
  const displayCodeValue = isGroupCode ? splRatesHiddenFields?.groupCode : splRatesHiddenFields?.corporateCode;

  const pointsToolTipNode = showPointsToolTip && isSecondaryBlockEnabled && (
    <div className="ng-special-rates-points-tooltip">
      <NGPointsToolTip
        onClose={hidePointsToolTip}
        pointsToolTipCTALabel={pointsToolTipCTALabel}
        pointsToolTipMessage={pointsToolTipMessage}
        pointsToolTipTitle={pointsToolTipTitle}
        autoCloseSeconds={15}
      />
    </div>
  );

  return (
    <StyledSpecialRatesDiv
      data-component-name="m-shop-SpecialRates"
      data-testid="shop-SpecialRates"
      className={clsx(
        'special-rates-block',
        !isNextGenHP && isDesktopView && isSearchFormActiveState ? 'is-active' : ''
      )}
      onClick={() => {
        !isDesktopView && !popupOpenState && showPopup();
      }}
      ref={inputRef}
      $buttonOffset={buttonOffset}
      $isNextGenHP={isNextGenHP}
      $isDesktopView={isDesktopView}
    >
      {isNextGenHP && isDesktopView ? (
        <div ref={customSelectRef} data-mini-trigger="rates">
          <div
            onClick={e => {
              if (e.target instanceof HTMLLabelElement) {
                e.preventDefault();
                e.stopPropagation();
                showPopup();
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();

                // Open the dropdown and submit form for NextGenHP
                showPopup();

                // Submit the form after opening dropdown
                setTimeout(() => {
                  const submitButton = document.querySelector(
                    'button[data-testid="searchform-cta-button"]'
                  ) as HTMLButtonElement;
                  if (submitButton) {
                    submitButton.click();
                  }
                }, 100);
              }
            }}
          >
            <CustomSelectBlock
              selectTitle={specialRatesEyebrowText}
              selectDescription={returnDescription()}
              handleClick={showPopup}
              name={specialRatesEyebrowText}
              id={specialRatesEyebrowText}
              isExpanded={popupOpenState}
              changeArrowOnOpen={true}
              customButtonClass="t-font-weight-m t-font-xs custom_click_track"
              customClickTrackValue={`${searchFormClickTrackingLoc} | Rates & Points |internal`}
              bothLabelArrowClick={false}
            ></CustomSelectBlock>
            {pointsToolTipNode}
          </div>
        </div>
      ) : (
        // Mobile view for both NextGenHP and NextGen standard, and desktop NextGen standard
        (() => {
          const isDesktopNextGenStandard = !isNextGenHP && isDesktopView;
          const inputField = (
            <>
              <InputTextField
                custom_click_track_value={`${searchFormClickTrackingLoc} | Rates & Points |internal`}
                {...(isDesktopNextGenStandard && { onBlur: handleBlur })}
                className="m-input-text-field special-rates-input-container custom_click_track" // these 2 class names are mandatory , please do not remove
                label={addLabelSpacingForArcTool ? specialRatesEyebrowText + ' ' : specialRatesEyebrowText}
                inputValue={returnDescription()}
                showIcon={isDesktopNextGenStandard ? isSearchFormActiveState : true}
                isClickEnabled={isDesktopNextGenStandard && true}
                iconClass={
                  isDesktopNextGenStandard ? (popupOpenState ? 'icon-arrow-up' : 'icon-arrow-down') : 'icon-arrow-down'
                }
                iconOnClick={handleIconPopUpClick}
                getInputProps={() => ({
                  readOnly: true,
                  onKeyDown: (ev: React.KeyboardEvent<HTMLInputElement>) => {
                    if (ev.key === 'Enter') {
                      // Handle inactive state: First Enter should activate the form (NextGen Standard only)
                      if (!isSearchFormActiveState && !isNextGenHP && isDesktopView) {
                        ev.preventDefault();
                        setIsSearchFormActiveState(true);
                        // Focus will remain on the same input after state change
                        setTimeout(() => {
                          // Use the event target directly
                          const specialRatesInput = ev.target as HTMLInputElement;
                          if (specialRatesInput) {
                            specialRatesInput.focus();
                          }
                        }, 100);
                        return;
                      }

                      // For NextGenHP or active state: open the dropdown and submit form
                      if (isNextGenHP || isSearchFormActiveState) {
                        onInteraction?.();
                        const newState = !popupOpenState;
                        setPopupOpenState(newState);
                        if (newState) {
                          setActivePopup('specialRates');
                        } else if (activePopup === 'specialRates') {
                          setActivePopup(null);
                        }

                        // Submit the form after opening dropdown
                        setTimeout(() => {
                          const submitButton = document.querySelector(
                            'button[data-testid="searchform-cta-button"]'
                          ) as HTMLButtonElement;
                          if (submitButton) {
                            submitButton.click();
                          }
                        }, 100);
                        return;
                      }

                      // Default behavior for other cases
                      onInteraction?.();
                      const newState = !popupOpenState;
                      setPopupOpenState(newState);
                      if (newState) {
                        setActivePopup('specialRates');
                      } else if (activePopup === 'specialRates') {
                        setActivePopup(null);
                      }
                    }
                  },
                })}
                readOnly={true}
                {...(isDesktopNextGenStandard && { isFocusEnabled: isSearchFormActiveState })}
                inputTextFieldClassName={clsx(
                  'special-rates-text',
                  // Add special-rates-input-field class for NextGen standard (both desktop and mobile)
                  // This enables the is-active/is-inactive styling
                  !isNextGenHP && 'special-rates-input-field',
                  isDesktopNextGenStandard && popupOpenState ? 'input-with-border' : ''
                )}
                variation={'default'}
                withinModal={!isDesktopView}
                showUnderline={!isDesktopView}
                {...(isDesktopNextGenStandard && { iconAriaLabel: 'Rates & Points Dropdown' })}
                trailingIconProps={{
                  ...(isDesktopNextGenStandard && {
                    className: clsx('trailing-element', 'special-rates-arrow-icon'), // these 2 class names are mandatory , please do not remove
                  }),
                  'aria-expanded': popupOpenState,
                  onKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.code === '13') {
                      onInteraction?.();
                      handleIconPopUpClick(e);
                    }
                  },
                }}
              ></InputTextField>

              {!isDesktopNextGenStandard && showPointsToolTip && (
                <NGPointsToolTip
                  onClose={hidePointsToolTip}
                  pointsToolTipCTALabel={pointsToolTipCTALabel}
                  pointsToolTipMessage={pointsToolTipMessage}
                  pointsToolTipTitle={pointsToolTipTitle}
                  toolTipOpenDelay={1}
                  customClass={clsx('w-100')}
                />
              )}
            </>
          );

          // Wrap with div for NextGen standard (is-active/is-inactive styling) - both desktop and mobile
          const isMobileNextGenStandard = !isNextGenHP && !isDesktopView;
          if (isDesktopNextGenStandard || isMobileNextGenStandard) {
            // For NextGen standard:
            // - Desktop: use isSearchFormActiveState from store
            // - Mobile inside modal: always active (modal = editing mode)
            // - Mobile outside modal: always inactive
            const shouldShowActiveState = isDesktopView
              ? isSearchFormActiveState
              : isInModal
              ? true // Mobile inside modal = always active
              : false; // Mobile outside modal = always inactive

            // Calculate width - only measure on client after mount to avoid SSR hydration mismatch
            const description = returnDescription();
            const inputWidth = isClientMounted && description ? measureTextWidth(description) + 16 : undefined;

            return (
              <div className={clsx(!isMobileNextGenStandard ? 'special-rates-tooltip-container' : '')}>
                <div
                  className={clsx('special-rates-input-wrapper', shouldShowActiveState ? 'is-active' : 'is-inactive')}
                  style={
                    !shouldShowActiveState && inputWidth
                      ? ({
                          '--input-ch-width': `${inputWidth}px`,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  {!shouldShowActiveState && (
                    <span className="special-rates-icon-wrapper">
                      <span className="icon-m icon-member-rates1x" />
                    </span>
                  )}
                  {inputField}
                </div>
                {isDesktopNextGenStandard && showPointsToolTip && isSearchFormActiveState && (
                  <NGPointsToolTip
                    onClose={hidePointsToolTip}
                    pointsToolTipCTALabel={pointsToolTipCTALabel}
                    pointsToolTipMessage={pointsToolTipMessage}
                    pointsToolTipTitle={pointsToolTipTitle}
                    autoCloseSeconds={15}
                    customClass="ng-points-tooltip-desktop"
                  />
                )}
              </div>
            );
          }
          return inputField;
        })()
      )}

      <div ref={compRef}>
        {popupOpenState && (
          <div>
            <DropDownModal
              shouldAutoFocusOnPopup={true}
              isAutoScrollEnabled={isNextGenHP ? true : false}
              parentElementTopValue={searchFormTopPosition?.value}
              applyDefaultHeight={true}
              className={clsx('dropdown-container', isNextGenHP && 'dropdown-container-nextGenHP')}
              childrenClassName={`special-rates-dropdown`}
              show={popupOpenState}
              applyLabel={specialRatesApplyCta || ''}
              applyTrackLabel={applyTrackLabel}
              applyBtnClickHandler={() => {
                // Set flag and immediately call onChange.
                // We bypass dropdownModalOnCLoseFunc entirely because DropDownModal calls it
                // BEFORE applyBtnClickHandler, so the flag would always read false there.
                applyClickedRef.current = true;
                onChange?.();
              }}
              dropDownSpecificFlag={true}
              mobileModalHeading={specialRatesEyebrowText}
              dropdownModalCloseClassName={'special-rates-close'}
              clickTrackingLoc={'Rates & Points dropdown'}
              role="dialog"
              callhandleBlur={showDesktopFooter ? undefined : handleBlur}
              // label={addLabelSpacingForArcTool ? specialRatesEyebrowText + ' ' : specialRatesEyebrowText}
              labelledBy={addLabelSpacingForArcTool ? 'container' : 'containerSecondary'}
              mobileTakeOver={!isDesktopView ? true : false}
              desktopDropdown={showDesktopFooter}
              handleBlur={showDesktopFooter ? false : isDesktopView ? true : false}
              mobileHeaderEnabled={true}
              mobileFooterEnabled={true}
              scrollDownModalCloseState={!showDesktopFooter}
              setDropdownModalOpenState={setPopupOpenState}
              dropdownModalOpenState={popupOpenState}
              dropdownModalOnCLoseFunc={() => {
                setTimeout(() => {
                  //using use timeout as ADA is not working without it
                  // Only call onCancel if Apply was NOT clicked (user clicked X to close)
                  if (!applyClickedRef.current) {
                    onCancel?.();
                  }
                  // Do NOT reset applyClickedRef here — DropDownModal calls this function
                  // twice for Apply clicks, causing a race. It resets on next forceOpen instead.
                  setPopupOpenState(false);
                  if (activePopup === 'specialRates') {
                    setActivePopup(null);
                  }
                }, 100);
              }}
              clearClickHandler={() => {
                setSearchFormState([SPECIAL_RATES], {
                  [SPECIAL_RATES]: {
                    specialRateCode: 'none',
                    corporateCode: '',
                    groupCode: '',
                    clusterCode: 'Lowest Regular Rate',
                  },
                });
                setShowCorpCodeInputField(false);
              }}
            >
              <>
                <div role="radiogroup">
                  {pointsAwardsTitle && (
                    <div className={clsx(' t-subtitle-m', !isDesktopView ? 'mt-5' : '')}>{pointsAwardsTitle}</div>
                  )}
                  {pointsSpecialRatesList?.map((element: Record<string, string>, index: number) => {
                    return (
                      <div
                        tabIndex={-1}
                        key={element['specialRatesCode']}
                        className={clsx('spl-rates-radio-container', 'm-radio-button-xs')}
                      >
                        <RadioButton
                          dataTestId={
                            element['specialRatesCode'] === 'corp'
                              ? 'option-radio-btn'
                              : element['specialRatesCode'] === 'group'
                              ? 'option-radio-btn-group'
                              : ''
                          }
                          //radiobuttonId={`clusterCode${index}`}
                          radiobuttonId={
                            addLabelSpacingForArcTool ? `clusterCode${index}` : `clusterCode${index}-secondary`
                          }
                          radiobuttonName={
                            addLabelSpacingForArcTool
                              ? element['specialRatesCode'] + 'tablet'
                              : element['specialRatesCode']
                          }
                          radiobuttonLabel={
                            addLabelSpacingForArcTool
                              ? element['specialRateslabel']
                              : `${element['specialRateslabel']} ` //do not remove this extra white space added at the end
                          }
                          customClickClass="custom_click_track"
                          checked={
                            element['specialRatesCode']?.toLowerCase() === 'points'
                              ? Boolean(usePointsValue?.rewardsRedemption)
                              : !usePointsValue?.rewardsRedemption &&
                                splRatesHiddenFields?.specialRateCode?.toLowerCase() ===
                                  element['specialRatesCode']?.toLowerCase()
                          }
                          onChange={({ target: { value } }): void =>
                            changeHandler(value, element['specialRateslabel'], element['specialRatesCode'])
                          }
                          value={element['specialRatesCode']}
                          custom_click_track_value={`${clickTrackingLoc}| ${element['specialRateslabel']} |internal`}
                          disabled={false}
                          setTabIndex={-1}
                          labelClassName={'fontWeight'}
                          className={clsx('radio-label', 'py-2 my-1')}
                          onKeyDown={(e, value, label) => onKeyDown(e, value, label, element['specialRatesCode'])}
                        />
                        {index === 0 && <div className={clsx('divider', 'mt-3 mb-3 mt-mb-3 mb-mb-2')}></div>}
                      </div>
                    );
                  })}
                </div>
                {/* Text field to display below radio button when corp code option is selected */}
                {shorCorpCodeInputField && (
                  <div
                    className={clsx(
                      'custom-input-padding',
                      !isAvailabilityScenario ? 'pt-1 tabbed-promo-code' : 'top-spacing-class',
                      isDesktopView ? 'crop-promo-code search-form-promo-code' : 'crop-promo-code'
                    )}
                  >
                    <InputTextField
                      label={enterCodeEyebrowText}
                      inputValue={displayCodeValue ?? ''}
                      getInputValue={handleInputChange}
                      handleKeyDown={keyHandler}
                      //TODO- Should be authorable
                      inputMaxLength={groupCodeMaxLength}
                      showIcon={showIcon || Boolean(displayCodeValue)}
                      iconClass={displayCodeValue?.length > 0 ? 'icon-clear icon-s custom_click_track' : ''}
                      className={clsx('custom_click_track', 'm-input-text-field')}
                      iconOnClick={clearInput}
                      infoLabel="special-rates"
                      variation={'default'}
                      withinModal={true}
                      showUnderline={true}
                      inputTextFieldClassName={!isDesktopView ? '' : 'desktop-font-text-class'}
                      trailingIconProps={{ onKeyDown: handleIconClick, role: 'button' }}
                      icon_custom_click_track_value={SPECIAL_RATES_CLOSE_CLICK_TRACK}
                      iconAriaLabel={'Clear'}
                      messageToShow={errorMessage?.alertMessage}
                      messageClass="error-message"
                    />
                    <div className={clsx('apply-button-desktop', 'd-flex', 'justify-content-end')}>
                      {isDesktopView && !showDesktopFooter && (
                        <button
                          type="button"
                          onClick={() => {
                            setPopupOpenState(false);
                            if (activePopup === 'specialRates') {
                              setActivePopup(null);
                            }
                          }}
                          className="m-button-secondary m-button-s custom_click_track"
                          custom_click_track_value={NG_SPECIAL_RATES_DONE_CLICK_TRACK}
                          {...(applyTrackLabel && { 'data-track-label': applyTrackLabel })}
                        >
                          {specialRatesApplyCta}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            </DropDownModal>
          </div>
        )}
      </div>
    </StyledSpecialRatesDiv>
  );
};
